import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemePreferenceService {
  private static readonly storageKey = 'birco.theme';

  private readonly _document = inject(DOCUMENT);
  private readonly _themeQuery = this._document.defaultView?.matchMedia?.(
    '(prefers-color-scheme: dark)',
  );
  private readonly _theme$ = new BehaviorSubject<ThemeMode>(
    this.resolveInitialTheme(),
  );
  private readonly _resolvedTheme$ = new BehaviorSubject<ResolvedTheme>(
    this.resolveResolvedTheme(this._theme$.value),
  );

  readonly theme$ = this._theme$.asObservable();
  readonly resolvedTheme$ = this._resolvedTheme$.asObservable();
  readonly isDarkMode$ = this.theme$.pipe(
    map((theme) => this.resolveResolvedTheme(theme) === 'dark'),
    distinctUntilChanged(),
  );

  constructor() {
    this.applyTheme();
    this.listenToSystemThemeChanges();
  }

  get theme(): ThemeMode {
    return this._theme$.value;
  }

  get themeMode(): ThemeMode {
    return this._theme$.value;
  }

  setTheme(theme: ThemeMode): void {
    if (theme === this.theme) {
      return;
    }

    this._theme$.next(theme);
    this.applyTheme();

    try {
      localStorage.setItem(ThemePreferenceService.storageKey, theme);
    } catch {
      // Ignore storage access issues to keep the UI usable.
    }
  }

  toggleTheme(): void {
    this.setTheme(this.resolveResolvedTheme(this.theme) === 'dark' ? 'light' : 'dark');
  }

  private resolveInitialTheme(): ThemeMode {
    const storedTheme = this.readStoredTheme();
    if (storedTheme) {
      return storedTheme;
    }

    return 'dark';
  }

  private readStoredTheme(): ThemeMode | null {
    try {
      const storedTheme = localStorage.getItem(ThemePreferenceService.storageKey);
      return storedTheme === 'dark' || storedTheme === 'light' || storedTheme === 'system'
        ? storedTheme
        : null;
    } catch {
      return null;
    }
  }

  private prefersDarkMode(): boolean {
    return !!this._themeQuery?.matches;
  }

  private resolveResolvedTheme(theme: ThemeMode): ResolvedTheme {
    return theme === 'system' ? (this.prefersDarkMode() ? 'dark' : 'light') : theme;
  }

  private applyTheme(): void {
    const root = this._document.documentElement;
    const theme = this.theme;
    const resolvedTheme = this.resolveResolvedTheme(theme);

    this._resolvedTheme$.next(resolvedTheme);

    root.setAttribute('data-theme', resolvedTheme);
    root.setAttribute('data-theme-mode', theme);
    root.style.colorScheme = resolvedTheme;
  }

  private listenToSystemThemeChanges(): void {
    if (!this._themeQuery) {
      return;
    }

    const handleChange = () => {
      if (this.theme === 'system') {
        this.applyTheme();
      }
    };

    if ('addEventListener' in this._themeQuery) {
      this._themeQuery.addEventListener('change', handleChange);
      return;
    }

    (this._themeQuery as MediaQueryList).addListener(handleChange);
  }
}
